// 阅读系统存储管理器接口定义

import type {
  BookSource,
  BookInfo,
  ReadingProgress,
  ReadingSettings,
  CacheType,
  UserDataType
} from '@/types/reading';

// ============================================================================
// 存储管理器接口
// ============================================================================

export interface StorageManager {
  // 初始化和配置
  initialize(): Promise<void>;
  clear(): Promise<void>;
  getStorageInfo(): Promise<StorageInfo>;
  
  // 书源存储
  saveBookSource(source: BookSource): Promise<void>;
  getBookSource(id: string): Promise<BookSource | null>;
  getBookSources(): Promise<BookSource[]>;
  updateBookSource(id: string, updates: Partial<BookSource>): Promise<void>;
  deleteBookSource(id: string): Promise<void>;
  
  // 图书信息存储
  saveBookInfo(book: BookInfo): Promise<void>;
  getBookInfo(id: string): Promise<BookInfo | null>;
  getAllBookInfo(): Promise<BookInfo[]>;
  getBooksByCategory(category: string): Promise<BookInfo[]>;
  getBooksBySource(sourceId: string): Promise<BookInfo[]>;
  searchBooks(query: string): Promise<BookInfo[]>;
  deleteBookInfo(id: string): Promise<void>;
  
  // 用户数据存储
  saveUserData(type: UserDataType, data: any, bookId?: string): Promise<void>;
  getUserData(type: UserDataType, bookId?: string): Promise<any>;
  deleteUserData(type: UserDataType, bookId?: string): Promise<void>;
  
  // 用户图书数据存储
  saveUserBookData(data: import('@/types/reading').UserBookData): Promise<void>;
  getUserBookData(bookId: string): Promise<import('@/types/reading').UserBookData | null>;
  getAllUserBookData(): Promise<import('@/types/reading').UserBookData[]>;
  deleteUserBookData(bookId: string): Promise<void>;
  
  // 图书集合存储
  saveBookCollection(collection: import('@/types/reading').BookCollection): Promise<void>;
  getBookCollection(id: string): Promise<import('@/types/reading').BookCollection | null>;
  getAllBookCollections(): Promise<import('@/types/reading').BookCollection[]>;
  deleteBookCollection(id: string): Promise<void>;
  
  // 搜索历史存储
  saveSearchHistory(history: import('@/types/reading').SearchHistory[]): Promise<void>;
  getSearchHistory(): Promise<import('@/types/reading').SearchHistory[]>;
  
  // 缓存管理
  setCacheItem(type: CacheType, key: string, data: any, ttl: number): Promise<void>;
  getCacheItem(type: CacheType, key: string): Promise<any>;
  clearExpiredCache(): Promise<void>;
  clearCacheByType(type: CacheType): Promise<void>;
}

export interface LocalStorageManager {
  // 用户设置
  saveUserSettings(settings: any): void;
  getUserSettings(): any;
  
  // 阅读设置
  saveReadingSettings(settings: ReadingSettings): void;
  getReadingSettings(): ReadingSettings;
  
  // 阅读进度（快速访问）
  saveReadingProgress(progress: ReadingProgress): void;
  getReadingProgress(bookId: string): ReadingProgress | null;
  
  // 简单配置数据
  saveConfig(key: string, value: any): void;
  getConfig(key: string): any;
  removeConfig(key: string): void;
  
  // 导入导出
  exportData(): string;
  importData(data: string): boolean;
}

// ============================================================================
// 存储信息类型
// ============================================================================

export interface StorageInfo {
  indexedDB: {
    used: number; // bytes
    available: number; // bytes
    quota: number; // bytes
  };
  localStorage: {
    used: number; // bytes
    available: number; // bytes
    quota: number; // bytes
  };
  cache: {
    itemCount: number;
    totalSize: number; // bytes
    expiredCount: number;
  };
}

// ============================================================================
// 数据库架构定义
// ============================================================================

export interface DatabaseSchema {
  version: number;
  stores: {
    bookSources: {
      keyPath: 'id';
      indexes: {
        name: 'name';
        url: 'url';
        enabled: 'enabled';
        updatedAt: 'updatedAt';
      };
    };
    books: {
      keyPath: 'id';
      indexes: {
        sourceId: 'sourceId';
        title: 'title';
        author: 'author';
        category: 'category';
        updatedAt: 'updatedAt';
      };
    };
    userData: {
      keyPath: 'id';
      indexes: {
        type: 'type';
        bookId: 'bookId';
        createdAt: 'createdAt';
      };
    };
    cache: {
      keyPath: 'id';
      indexes: {
        type: 'type';
        key: 'key';
        expiresAt: 'expiresAt';
        createdAt: 'createdAt';
      };
    };
  };
}

// ============================================================================
// 存储配置
// ============================================================================

export interface StorageConfig {
  databaseName: string;
  databaseVersion: number;
  cacheExpiration: {
    chapter: number; // hours
    image: number; // hours
    search: number; // hours
    bookInfo: number; // hours
    category: number; // hours
  };
  quotaLimits: {
    indexedDB: number; // MB
    localStorage: number; // MB
    cache: number; // MB
  };
  cleanupInterval: number; // ms
}

// ============================================================================
// 默认配置
// ============================================================================

export const DEFAULT_STORAGE_CONFIG: StorageConfig = {
  databaseName: 'ReadingSystemDB',
  databaseVersion: 1,
  cacheExpiration: {
    chapter: 24, // 24 hours
    image: 168, // 1 week
    search: 1, // 1 hour
    bookInfo: 24, // 24 hours
    category: 168 // 1 week
  },
  quotaLimits: {
    indexedDB: 500, // 500 MB
    localStorage: 10, // 10 MB
    cache: 200 // 200 MB
  },
  cleanupInterval: 60 * 60 * 1000 // 1 hour
};

export const DEFAULT_READING_SETTINGS: ReadingSettings = {
  fontSize: 16,
  fontFamily: 'system-ui, -apple-system, sans-serif',
  backgroundColor: '#ffffff',
  textColor: '#333333',
  lineHeight: 1.6,
  pageWidth: 800,
  nightMode: false,
  autoScroll: false,
  scrollSpeed: 1,
  pageAnimation: true
};

// ============================================================================
// 存储事件类型
// ============================================================================

export interface StorageEvent {
  type: StorageEventType;
  timestamp: Date;
  data?: any;
}

export const StorageEventType = {
  INITIALIZED: 'INITIALIZED',
  QUOTA_WARNING: 'QUOTA_WARNING',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  CACHE_CLEANED: 'CACHE_CLEANED',
  DATA_CORRUPTED: 'DATA_CORRUPTED',
  MIGRATION_COMPLETED: 'MIGRATION_COMPLETED'
} as const;

export type StorageEventType = typeof StorageEventType[keyof typeof StorageEventType];

// ============================================================================
// 工具函数接口
// ============================================================================

export interface StorageUtils {
  generateId(): string;
  calculateSize(data: any): number;
  compressData(data: any): Promise<string>;
  decompressData(compressed: string): Promise<any>;
  validateData(data: any, schema: any): boolean;
  migrateData(oldVersion: number, newVersion: number, data: any): Promise<any>;
}